import {
  Component,
  effect,
  EffectRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormsModule, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { PasswordManagerService } from '@core/services/password-manager.service';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonDisableDirective } from '@shared/directives/button-disable.directive';

import {
  ButtonComponent,
  IGenCatForm,
  NgGenCatFormComponent,
} from '@ctti-dev/ng-gencat';
import { CallOutComponent } from '@shared/components/callout/callout.component';

import { Callout } from '@shared/models/callout.model';
import {
  REQUEST_PASSWORD_RESET_FORM_CONFIG,
  RESET_PASSWORD_ERROR_CALLOUT,
  RESET_PASSWORD_INFO_CALLOUT,
} from './configurations/reset-password.config';

import { Observable, takeUntil, Subject, timer } from 'rxjs';
import { ButtonConfigDirective } from '@shared/directives/button-config.directive';
import { PRIMARY_BUTTON } from '@shared/utils/utils.config';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [
    FormsModule,
    ButtonComponent,
    CallOutComponent,
    NgGenCatFormComponent,
    ButtonDisableDirective,
    TranslatePipe,
    ButtonConfigDirective,
  ],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  resetPasswordForm!: UntypedFormGroup;
  alreadyVerified = false;

  readonly primaryButton = PRIMARY_BUTTON;
  readonly resetPasswordFormConfig: IGenCatForm =
    REQUEST_PASSWORD_RESET_FORM_CONFIG;
  readonly resetPasswordInfoToastConfig: Partial<Callout> =
    RESET_PASSWORD_INFO_CALLOUT;
  readonly resetPasswordErrorToastConfig: Partial<Callout> =
    RESET_PASSWORD_ERROR_CALLOUT;

  readonly readyResetPasswordSignal: WritableSignal<boolean> =
    signal<boolean>(false);
  readonly showResetPasswordInfoToastSignal: WritableSignal<boolean> =
    signal<boolean>(true);

  readonly resetPasswordNewPasswordSignal: WritableSignal<string> =
    signal<string>('');

  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _passwordManagerService = inject(PasswordManagerService);

  private readonly _destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.getResetPasswordParamsFromRoute();
  }

  verifyUserToken(): void {
    this._passwordManagerService
      .callTokenVerification()
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        error: () => {
          this.resetPasswordRedirect();
        },
      });
  }

  resetPasswordFormLoaded(form: UntypedFormGroup) {
    this.resetPasswordForm = form;
    this.handleResetPasswordsValueChanges();
  }

  resetPassword(): void {
    const { newPassword } = this.resetPasswordForm.getRawValue();
    this.resetPasswordNewPasswordSignal.set(newPassword);

    const username = this._passwordManagerService.resetPasswordUsernameSignal();
    const token = this._passwordManagerService.resetPasswordTokenSignal();
    const password = this.resetPasswordNewPasswordSignal();

    if (username && password && token) {
      this._passwordManagerService.requestUserPasswordReset(
        username,
        password,
        token,
      );
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _tokenVerificationEffect: EffectRef = effect(
    () => {
      const token: string =
        this._passwordManagerService.resetPasswordTokenSignal();
      const poolId: string =
        this._passwordManagerService.resetPasswordUserPoolIdSignal();
      if (token !== '' && poolId !== '' && !this.alreadyVerified) {
        this.verifyUserToken();
        this.alreadyVerified = true;
      }
    },
    { allowSignalWrites: true },
  );

  private handleResetPasswordsValueChanges(): void {
    this.resetPasswordForm.valueChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        const { newPassword, confirmNewPassword } =
          this.resetPasswordForm.getRawValue();

        const equalPasswords: boolean = newPassword === confirmNewPassword;
        const filledBothPasswordFields: boolean =
          newPassword && confirmNewPassword;

        this.readyResetPasswordSignal.set(
          equalPasswords && filledBothPasswordFields,
        );

        this.showResetPasswordInfoToastSignal.set(
          !filledBothPasswordFields || equalPasswords,
        );
      });
  }

  private getResetPasswordParamsFromRoute(): void {
    this._passwordManagerService.resetPasswordUsernameSignal.set(
      this._route.snapshot.queryParams['username'],
    );
    this._passwordManagerService.resetPasswordTokenSignal.set(
      this._route.snapshot.queryParams['token'],
    );
    this._passwordManagerService.resetPasswordUserPoolIdSignal.set(
      this._route.snapshot.queryParams['userPoolId'],
    );
  }

  private resetPasswordRedirect(): void {
    const timer$: Observable<number> = timer(3000);
    timer$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this._router.navigateByUrl('/login');
    });
  }
}
